/**
 * useAuth Hook
 * 
 * Custom React hook for accessing authentication state and actions.
 * Provides easy access to user info, authentication status, and logout functionality.
 */

import { useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout as logoutAction } from '../store/authSlice';
import { Permission } from '@/shared/application/types/authTypes';
import Account from '@/features/account/application/domain/entities/account';
import { AppUser } from '@/features/app-user/application/domain/entities/account';

interface UseAuthReturn {
  /** Current authenticated user (null if not authenticated) */
  user: AppUser | null;

  /** Current authenticated user's account (null if not authenticated) */
  account: Account | null;

  /** Current authenticated user's role (null if not authenticated) */
  role: string | null;

  /** User's permissions array */
  permissions: Permission[];

  /** Whether user is currently authenticated */
  isAuthenticated: boolean;

  /** Whether user has selected an organization/account */
  hasSelectedAccount: boolean;

  /** Loading state for auth operations */
  loading: boolean;

  /** Error message if auth operation failed */
  error: string | null;

  /** Function to log out the current user */
  logout: () => void;

  isRemoteI9User: boolean;
}

/**
 * Hook to access authentication state and actions
 * 
 * @returns Auth state and logout function
 * 
 * @example
 * ```tsx
 * function UserProfile() {
 *   const { user, isAuthenticated, logout } = useAuth();
 *   
 *   if (!isAuthenticated) {
 *     return <div>Not logged in</div>;
 *   }
 *   
 *   return (
 *     <div>
 *       <p>Welcome, {user?.username}!</p>
 *       <button onClick={logout}>Log out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const dispatch = useAppDispatch();

  // Select auth state from Redux
  const {
    user,
    account,
    role,
    permissions,
    isAuthenticated,
    hasSelectedAccount,
    loading,
    error,
    isRemoteI9User,
  } = useAppSelector((state) => state.auth);

  // Memoized logout function
  const logout = useCallback(() => {
    dispatch(logoutAction());
  }, [dispatch]);

  // If authenticated but has no role or account, force logout
  useEffect(() => {
    // "No role assigned" must be judged by the normalized roleKey (VTK-1828), not the display string
    // `vtAppRole` — a super_admin whose `vtAppRole` is null/snake-case would otherwise be force-logged-out.
    if (!loading && isAuthenticated && !role && !user?.account) {
      logout();
    }
  }, [loading, isAuthenticated, role, user, logout]);

  return {
    user,
    account,
    role,
    permissions,
    isAuthenticated,
    hasSelectedAccount,
    loading,
    error,
    logout,
    isRemoteI9User,
  };
}
